import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { useOnlineStatus, useQueueStatus } from '@/hooks/useKiosk';

export interface OfflineBannerProps {
  /** Override Date.now() for deterministic relative-time tests. */
  now?: () => number;
}

function formatRelative(timestamp: number, now: number): string {
  const deltaSec = Math.max(0, Math.round((now - timestamp) / 1000));
  if (deltaSec < 60) return 'gerade eben';
  const minutes = Math.round(deltaSec / 60);
  if (minutes < 60) return `vor ${minutes} Min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `vor ${hours} Std`;
  const days = Math.round(hours / 24);
  return `vor ${days} Tg`;
}

/**
 * Online/Offline-Indikator-Banner per Prompt 7.
 *
 * Sichtbar wenn:
 *   - offline (auch ohne wartende Scans), oder
 *   - online + Queue non-leer.
 *
 * Online + Queue leer → kein Banner.
 */
export function OfflineBanner({ now }: OfflineBannerProps): JSX.Element | null {
  const online = useOnlineStatus();
  const status = useQueueStatus();
  const [tick, setTick] = useState<number>(() => (now ? now() : Date.now()));

  useEffect(() => {
    if (!status.lastSyncAt) {
      setTick(now ? now() : Date.now());
      return;
    }
    setTick(now ? now() : Date.now());
    const interval = setInterval(() => {
      setTick(now ? now() : Date.now());
    }, 30_000);
    return () => clearInterval(interval);
  }, [status.lastSyncAt, now]);

  const visible = !online || status.pending > 0;
  if (!visible) return null;

  const hasPending = status.pending > 0;
  const lastSyncLabel = status.lastSyncAt ? formatRelative(status.lastSyncAt, tick) : null;
  const headline = !online
    ? hasPending
      ? `Offline-Modus aktiv · ${status.pending} Scan${status.pending === 1 ? '' : 's'} warte${status.pending === 1 ? 't' : 'n'}`
      : 'Offline-Modus aktiv'
    : `Synchronisiere · ${status.pending} Scan${status.pending === 1 ? '' : 's'} ausstehend`;

  return (
    <div
      role="status"
      data-testid="offline-banner"
      data-online={online ? 'true' : 'false'}
      data-pending={status.pending}
      className={cn(
        'fixed inset-x-0 bottom-0 z-40 flex items-center justify-center gap-3 px-4 py-2 text-sm font-medium shadow-md',
        online ? 'bg-amber-100 text-amber-900' : 'bg-slate-800 text-slate-100',
      )}
    >
      <span aria-hidden>{online ? '↻' : '⏱'}</span>
      <span>{headline}</span>
      {lastSyncLabel ? <span className="opacity-80">· Letzter Sync {lastSyncLabel}</span> : null}
    </div>
  );
}
